import { FirebaseClientConfigSchema, parseEnv } from '@kilat/shared';
import { z } from 'zod';

const RuntimeEnvSchema = z.object({
  // 'e2e' is a real Vite mode, not a stray value: the Playwright harness runs
  // `vite --mode e2e` so that apps/<app>/.env.e2e is loaded, which Vite ranks
  // ABOVE a developer's gitignored .env.local. That is the only reliable way to
  // stop an e2e run from picking up local settings and talking to the real
  // project. Omitting it here does not "keep the contract tight" — it makes the
  // app throw at boot under the harness.
  MODE: z.enum(['development', 'production', 'test', 'e2e']),
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),
  VITE_USE_EMULATORS: z
    .union([z.literal('true'), z.literal('false')])
    .default('false')
    .transform((v) => v === 'true'),
  VITE_RECAPTCHA_SITE_KEY: z
    .string()
    .optional()
    .describe('reCAPTCHA Enterprise site key for App Check (prod)'),
  VITE_DASHBOARD_ORIGIN: z
    .string()
    .default('https://dashboard.kilatcarwash.net')
    .describe('Where to redirect after operator signup completes'),
  VITE_AGENT_ORIGIN: z
    .string()
    .default('https://agent.kilatcarwash.net')
    .describe('Where to redirect after agent signup completes'),
});

const raw = parseEnv(RuntimeEnvSchema, import.meta.env, 'marketing');

export const env = {
  mode: raw.MODE,
  useEmulators: raw.VITE_USE_EMULATORS,
  recaptchaSiteKey: raw.VITE_RECAPTCHA_SITE_KEY,
  dashboardOrigin: raw.VITE_DASHBOARD_ORIGIN,
  agentOrigin: raw.VITE_AGENT_ORIGIN,
  firebase: parseEnv(
    FirebaseClientConfigSchema,
    {
      apiKey: raw.VITE_FIREBASE_API_KEY,
      authDomain: raw.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: raw.VITE_FIREBASE_PROJECT_ID,
      storageBucket: raw.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: raw.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: raw.VITE_FIREBASE_APP_ID,
    },
    'marketing.firebase',
  ),
} as const;
export type Env = typeof env;
